import {
    useState,
    useEffect,
    useCallback,
    ChangeEventHandler,
    KeyboardEventHandler,
} from 'react';
import { useStore } from '@ui/hooks/useStore';
import { checkPassword } from '@utils/dataCheck';
import useKeyDown from '@ui/hooks/useKeyDown';
import { IState } from './IState';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';

const useEditSubuser = (onClose: ICloseEvent['onClose']) => {
    const { subusers, lang } = useStore();
    const key = useKeyDown();
    const login = subusers.selectedSubuser ?? '';

    const initState: IState = {
        login,
        password: '',
        passwordError: false,
        passwordConfirm: '',
        passwordConfirmError: false,
    };
    const [state, setState] = useState(initState);

    const passwordValidate = (password: string) => {
        const isPasswordValid = checkPassword(password);
        if (!isPasswordValid) {
            setState((prev) => ({
                ...prev,
                passwordError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                passwordError: false,
            }));
            return true;
        }
    };
    const passwordConfirmValidate = (password: string, confirm: string) => {
        if (password !== confirm) {
            setState((prev) => ({
                ...prev,
                passwordConfirmError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                passwordConfirmError: false,
            }));
            return true;
        }
    };
    const handlePasswordChange: ChangeEventHandler<HTMLInputElement> =
        useCallback((e) => {
            const password = e.target.value;
            setState((prev) => ({
                ...prev,
                password,
                passwordError: false,
                passwordConfirmError: false,
            }));
        }, []);
    const handlePasswordConfirmChange: ChangeEventHandler<HTMLInputElement> =
        useCallback((e) => {
            const passwordConfirm = e.target.value;
            setState((prev) => ({
                ...prev,
                passwordConfirm,
                passwordError: false,
                passwordConfirmError: false,
            }));
        }, []);
    const handleSaveClick = useCallback(() => {
        const { password, passwordConfirm } = state;
        const isPasswordValid = passwordValidate(password);
        const isConfirmValid = passwordConfirmValidate(
            password,
            passwordConfirm,
        );
        subusers.resetServerError();
        if (isPasswordValid && isConfirmValid) {
            subusers.changePassword(password);
        }
    }, [state.password, state.passwordConfirm]);
    const handleInputClick = useCallback(() => {
        subusers.resetServerError();
        setState((prev) => ({
            ...prev,
            passwordError: false,
            passwordConfirmError: false,
        }));
    }, []);
    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
            case 'Enter':
                handleSaveClick();
                break;
        }
    };
    const handleBtnKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
        (e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                const target = e.target as HTMLElement;
                target.click();
            }
        },
        [],
    );
    const handleKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
        (e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                handleKey(key);
            }
        },
        [state.password, state.passwordConfirm],
    );
    const handlers = {
        handlePasswordChange,
        handlePasswordConfirmChange,
        handleSaveClick,
        handleInputClick,
        handleKeyDown,
        handleBtnKeyDown,
    };

    useEffect(() => {
        subusers.resetServerError();
    }, []);
    useEffect(() => {
        handleKey(key);
    }, [key]);
    useEffect(() => {
        if (subusers.operationSuccess) {
            subusers.resetOperationSuccess();
            onClose();
        }
    }, [subusers.operationSuccess]);
    useEffect(() => {
        if (!subusers.selectedSubuser && !subusers.loading) onClose();
    }, [subusers.selectedSubuser, subusers.loading]);

    return {
        subusers,
        lang,
        state,
        handlers,
    };
};

export default useEditSubuser;
